/**
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import './TextInput.scss';

/**
 * This is the TextInput component.
 *
 * @author Magnus Bergman <magnus@apt.no>
 *
 * @return {JSX}
 */
function TextInput({ field, form, label, className, ...props }) {
  return (
    <div className="property-contact-form-input-group">
      <label
        htmlFor={field.name}
        className="label"
        dangerouslySetInnerHTML={{ __html: label }}
      />
      <div className="input-wrapper">
        {props.type === 'textarea' ? (
          <textarea
            id={props.id || field.name}
            name={field.name}
            onBlur={field.onBlur}
            onChange={field.onChange}
            value={field.value}
            rows="10"
            cols="40"
            className={classNames('input', className, {
              error: form.touched[field.name] && form.errors[field.name],
            })}
          ></textarea>
        ) : (
          <input
            id={props.id || field.name}
            name={field.name}
            type={props.type}
            value={field.value}
            onBlur={field.onBlur}
            onChange={field.onChange}
            className={classNames('input', className, {
              error: form.touched[field.name] && form.errors[field.name],
            })}
          />
        )}
        <p className="error-message" aria-live="polite">
          {form.errors[field.name]}
        </p>
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
TextInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  className: PropTypes.string,
  type: PropTypes.string.isRequired,
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string.isRequired,
      PropTypes.bool.isRequired,
    ]),
    onBlur: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  form: PropTypes.object.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
TextInput.defaultProps = {
  className: '',
  label: '',
};

export default TextInput;
